/**
 * No-Match Modal
 *
 * Accessible centered modal shown when a COMPLETE variation selection resolves
 * to no variation. Replaces WooCommerce's suppressed "no products matched"
 * message (see wc-override.js) and the earlier inline notice.
 *
 * Design: approved "Direction A — Centered Modal" prototype, themed to the
 * active store (font inherited; accent via --hvp-nm-accent). Blocking overlay
 * (backdrop click does NOT dismiss — it nudges); closes via ×, ESC, or a CTA.
 *
 * Audience-aware: logged-in admins (window.hvpData.isAdmin) additionally see an
 * amber note explaining the cache/stale-data reason + a "Reload options"
 * primary CTA. Customers get the simple "change a selection" message.
 *
 * Pure DOM-event consumer (no controller calls):
 *   hvp_no_match          -> open  { selections, attemptedAttributes, reason }
 *   hvp_variation_found   -> close (a valid combo resolved)
 *   hvp_reset             -> close
 *   hvp_selection_changed -> { complete } ; close when complete === false
 *
 * @package HaruVariationsPro
 * @version 2.0.0
 */

import { readCurrentSelection } from './no-match-selection.js';

const OVERLAY_CLASS = 'hvp-nm-overlay';

const FALLBACK = {
    noMatchEyebrow: 'Unavailable combination',
    noMatchTitle: "This combination isn't available",
    noMatchBody: 'Please change one of your selections above.',
    noMatchChange: 'Change a selection',
    noMatchReload: 'Reload options',
    noMatchClose: 'Close',
    noMatchYourSelection: 'Your current selection',
    noMatchTransportEyebrow: 'Connection problem',
    noMatchTransportTitle: "We couldn't reach the server",
    noMatchTransportBody:
        'Your session may have expired or the connection dropped, so we couldn\'t confirm this combination. Please reload the page and try again.',
    adminPill: 'Admin',
    adminNoteTitle: "Why you're seeing this as an admin",
    adminNoteBody:
        "You're logged in as an administrator, so you bypass the variation cache and may see product data that differs from what customers see. If you expected this combination to exist, the cached options may be stale. Reload to refresh the options, or purge the cache.",
};

/** Resolve a localized string with a safe fallback. */
function str(key) {
    const i18n = (window.hvpData && window.hvpData.i18n) || {};
    return i18n[key] || FALLBACK[key] || '';
}

const WARN_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
const CLOSE_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';

class NoMatchModal {
    constructor() {
        this.dom = null;
        this.lastFocus = null;
        this.reduceMQ = window.matchMedia('(prefers-reduced-motion:reduce)');
        this.isAdmin = !!(window.hvpData && window.hvpData.isAdmin);
        this.reason = 'no_combination';

        this.handleNoMatch = this.handleNoMatch.bind(this);
        this.handleResolved = this.handleResolved.bind(this);
        this.handleSelectionChanged = this.handleSelectionChanged.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBackdrop = this.onBackdrop.bind(this);
    }

    /** Attach listeners once. */
    init() {
        if (window.__hvp_no_match_modal_ready) {
            return;
        }
        window.__hvp_no_match_modal_ready = true;

        document.addEventListener('hvp_no_match', this.handleNoMatch);
        document.addEventListener('hvp_variation_found', this.handleResolved);
        document.addEventListener('hvp_reset', this.handleResolved);
        document.addEventListener('hvp_selection_changed', this.handleSelectionChanged);
    }

    handleNoMatch(event) {
        const reason = (event && event.detail && event.detail.reason) || 'no_combination';
        this.open(reason);
    }

    handleResolved() {
        this.close();
    }

    handleSelectionChanged(event) {
        // The selection dropped back to incomplete — the modal no longer applies.
        if (!(event && event.detail && event.detail.complete)) {
            this.close();
        }
    }

    /** Build the modal DOM on first use and cache element references. */
    ensureDom() {
        if (this.dom) {
            return this.dom;
        }

        const logoUrl = (window.hvpData && window.hvpData.logoUrl) || '';
        const overlay = document.createElement('div');
        overlay.className = OVERLAY_CLASS;
        overlay.setAttribute('data-state', 'customer');

        overlay.innerHTML =
            '<div class="hvp-nm-modal" role="alertdialog" aria-modal="true" aria-labelledby="hvp-nm-title" aria-describedby="hvp-nm-text">' +
                '<div class="hvp-nm-head">' +
                    (logoUrl ? '<img class="hvp-nm-logo" alt="">' : '') +
                    (logoUrl ? '<span class="hvp-nm-divider" aria-hidden="true"></span>' : '') +
                    '<span class="hvp-nm-glyph" aria-hidden="true">' + WARN_SVG + '</span>' +
                    '<span class="hvp-nm-eyebrow"></span>' +
                    '<button type="button" class="hvp-nm-close">' + CLOSE_SVG + '</button>' +
                '</div>' +
                '<div class="hvp-nm-body">' +
                    '<h2 class="hvp-nm-title" id="hvp-nm-title"></h2>' +
                    '<p class="hvp-nm-text" id="hvp-nm-text"></p>' +
                    '<div class="hvp-nm-ctx" hidden>' +
                        '<div class="hvp-nm-ctx-name"></div>' +
                        '<div class="hvp-nm-ctx-rows"></div>' +
                    '</div>' +
                    '<div class="hvp-nm-admin" role="note">' +
                        '<div class="hvp-nm-admin-head">' +
                            '<span class="hvp-nm-admin-pill"></span>' +
                            '<span class="hvp-nm-admin-title"></span>' +
                        '</div>' +
                        '<p class="hvp-nm-admin-body"></p>' +
                    '</div>' +
                    '<div class="hvp-nm-actions">' +
                        '<button type="button" class="hvp-nm-btn hvp-nm-btn--secondary"></button>' +
                        '<button type="button" class="hvp-nm-btn hvp-nm-btn--primary"></button>' +
                    '</div>' +
                '</div>' +
            '</div>';

        const $ = (s) => overlay.querySelector(s);
        const modal = $('.hvp-nm-modal');
        const logo = $('.hvp-nm-logo');
        if (logo) {
            logo.src = logoUrl;
        }

        // Static i18n text (set via textContent — XSS-safe).
        $('.hvp-nm-eyebrow').textContent = str('noMatchEyebrow');
        $('.hvp-nm-title').textContent = str('noMatchTitle');
        $('.hvp-nm-text').textContent = str('noMatchBody');
        $('.hvp-nm-close').setAttribute('aria-label', str('noMatchClose'));
        $('.hvp-nm-admin-pill').textContent = str('adminPill');
        $('.hvp-nm-admin-title').textContent = str('adminNoteTitle');
        $('.hvp-nm-admin-body').textContent = str('adminNoteBody');

        const secondaryBtn = $('.hvp-nm-btn--secondary');
        const primaryBtn = $('.hvp-nm-btn--primary');
        secondaryBtn.textContent = str('noMatchChange');

        $('.hvp-nm-close').addEventListener('click', () => this.close());
        secondaryBtn.addEventListener('click', () => this.changeSelection());
        primaryBtn.addEventListener('click', () => this.primaryAction());
        overlay.addEventListener('mousedown', this.onBackdrop);

        document.body.appendChild(overlay);

        this.dom = {
            overlay, modal, primaryBtn, secondaryBtn,
            ctx: $('.hvp-nm-ctx'),
            ctxName: $('.hvp-nm-ctx-name'),
            ctxRows: $('.hvp-nm-ctx-rows'),
        };
        return this.dom;
    }

    /** Populate the context card from the current selection. */
    populate() {
        const { ctx, ctxName, ctxRows } = this.dom;
        const { name, rows } = readCurrentSelection();

        if (!rows.length) {
            ctx.setAttribute('hidden', 'hidden');
            return;
        }
        ctxName.textContent = name || str('noMatchYourSelection');
        ctxRows.textContent = '';
        rows.forEach((r) => {
            const row = document.createElement('div');
            row.className = 'hvp-nm-ctx-row';
            const k = document.createElement('span');
            k.className = 'k';
            k.textContent = r.label;
            const v = document.createElement('span');
            v.className = 'v';
            v.textContent = r.value;
            row.appendChild(k);
            row.appendChild(v);
            ctxRows.appendChild(row);
        });
        ctx.removeAttribute('hidden');
    }

    open(reason = 'no_combination') {
        this.reason = reason;
        // Read audience fresh each open (PHP sets it via current_user_can).
        this.isAdmin = !!(window.hvpData && window.hvpData.isAdmin);
        const isTransport = reason === 'transport';
        const dom = this.ensureDom();

        // A transport failure (stale nonce / dropped connection) is NOT a proven
        // dead-end — we never got an answer. Re-skin the modal to a reload-first
        // message for everyone; otherwise use the standard no-combination copy.
        dom.modal.querySelector('.hvp-nm-eyebrow').textContent =
            isTransport ? str('noMatchTransportEyebrow') : str('noMatchEyebrow');
        dom.modal.querySelector('.hvp-nm-title').textContent =
            isTransport ? str('noMatchTransportTitle') : str('noMatchTitle');
        dom.modal.querySelector('.hvp-nm-text').textContent =
            isTransport ? str('noMatchTransportBody') : str('noMatchBody');
        dom.overlay.setAttribute('data-state', isTransport ? 'transport' : (this.isAdmin ? 'admin' : 'customer'));
        // Reload is the way out of a transport failure (fresh nonce) and the
        // admin's stale-cache CTA; a customer on a genuine no-combo changes a selection.
        dom.primaryBtn.textContent = (isTransport || this.isAdmin) ? str('noMatchReload') : str('noMatchChange');
        this.populate();

        this.lastFocus = document.activeElement;
        dom.overlay.classList.add('is-open');
        document.body.classList.add('hvp-nm-lock');
        document.addEventListener('keydown', this.onKeydown);

        const delay = this.reduceMQ.matches ? 0 : 170;
        window.setTimeout(() => {
            try { dom.primaryBtn.focus({ preventScroll: true }); } catch (e) { dom.primaryBtn.focus(); }
        }, delay);
    }

    close() {
        if (!this.dom || !this.dom.overlay.classList.contains('is-open')) {
            return;
        }
        this.dom.overlay.classList.remove('is-open');
        document.body.classList.remove('hvp-nm-lock');
        document.removeEventListener('keydown', this.onKeydown);
        if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
            try { this.lastFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        }
    }

    primaryAction() {
        // Reload on a transport failure (gets a fresh nonce/connection) or for an
        // admin (refreshes the bypassed cache) — both keep the current URL.
        if (this.reason === 'transport' || this.isAdmin) {
            window.location.reload();
            return;
        }
        // Customer primary on a genuine no-combination = "Change a selection".
        this.changeSelection();
    }

    /**
     * "Change a selection": clear ALL selected options and reload to a fresh
     * product page. Navigating to the bare pathname drops any attribute_* query
     * params, so the page comes back with nothing pre-selected.
     */
    changeSelection() {
        window.location.assign(window.location.pathname);
    }

    /** Backdrop click does NOT dismiss (blocking) — nudge the card instead. */
    onBackdrop(e) {
        if (e.target !== this.dom.overlay || this.reduceMQ.matches || !this.dom.modal.animate) {
            return;
        }
        this.dom.modal.animate(
            [
                { transform: 'translateX(0)' },
                { transform: 'translateX(-5px)' },
                { transform: 'translateX(5px)' },
                { transform: 'translateX(0)' },
            ],
            { duration: 220, easing: 'ease-in-out' }
        );
    }

    /** ESC closes; Tab is trapped within the modal. */
    onKeydown(e) {
        if (e.key === 'Escape') {
            this.close();
            return;
        }
        if (e.key !== 'Tab') {
            return;
        }
        const focusables = Array.from(this.dom.modal.querySelectorAll('button'))
            .filter((el) => el.offsetParent !== null);
        if (!focusables.length) {
            return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }
}

/** Initialize the singleton no-match modal. Safe to call multiple times. */
export function initNoMatchNotice() {
    const modal = new NoMatchModal();
    modal.init();
    return modal;
}

export { NoMatchModal };
